if (!customElements.get('country-selector')) {
  class CountrySelector extends HTMLElement {
    constructor() {
      super();
      this.provinceElement = this.querySelector('.js-shipping-province');
      this.countryElement = this.querySelector('.js-shipping-country');
      this.provinceWrapper = this.querySelector('.js-province-container');
      window.initScriptOnDemand(this, this.init.bind(this));
    }

    init() {
      CountrySelector.selectOption(this.countryElement, this.countryElement.dataset.default);

      this.updateProvinceOptions();

      if (this.provinceElement.dataset.default && this.provinceElement.options.length > 0) {
        CountrySelector.selectOption(this.provinceElement, this.provinceElement.dataset.default);
      }

      this.countryElement.addEventListener('change', this.updateProvinceOptions.bind(this));
    }

    /**
     * Updates the province/state options based on selected country.
     */
    updateProvinceOptions() {
      const selectedCountry = this.countryElement.options[this.countryElement.selectedIndex];
      const provinceList = JSON.parse(selectedCountry.dataset.provinces);

      // Clear existing options in the province field.
      Array.from(this.provinceElement.options).forEach((option) => option.remove());

      if (provinceList && provinceList.length === 0) {
        this.provinceWrapper.hidden = true;
      } else {
        provinceList.forEach((province) => {
          const provinceOption = document.createElement('option');
          [provinceOption.value, provinceOption.innerHTML] = province;
          this.provinceElement.appendChild(provinceOption);
        });

        this.provinceWrapper.hidden = false;
      }
    }

    /**
     * Sets the selected option of a <select> element.
     * @param {Element} dropdown - Country or province <select> element.
     * @param {string} value - The value to be selected in the dropdown.
     */
    static selectOption(dropdown, value) {
      Array.from(dropdown.options).forEach((option, index) => {
        if (option.value === value || option.innerHTML === value) {
          dropdown.selectedIndex = index;
        }
      });
    }
  }

  customElements.define('country-selector', CountrySelector);
}

if (!customElements.get('shipping-calculator')) {
  class ShippingCalculator extends HTMLElement {
    constructor() {
      super();
      this.submitButton = this.querySelector('button');
      this.shippingForm = this.querySelector('form');
      this.shippingForm.addEventListener('submit', this.processFormSubmit.bind(this));
      this.shippingForm.addEventListener('input', () => this.resetEstimate());
      this.shippingForm.addEventListener('change', () => this.resetEstimate());
    }

    connectedCallback() {
      if (typeof subscribe === 'function') {
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.resetEstimate());
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.unsubscribeCart?.();
    }

    resetEstimate() {
      this.controller?.abort();
      this.querySelector('.js-shipping-rate').hidden = true;
      this.querySelector('.js-shipping-error').hidden = true;
    }

    async processFormSubmit(event) {
      event.preventDefault();
      if (this.submitButton.disabled) return;
      this.resetEstimate();
      const country = this.querySelector('.js-shipping-country');
      const selectedCountry = country.options[country.selectedIndex];
      if (country.value !== this.dataset.marketCountry && selectedCountry.textContent.trim() !== this.dataset.marketCountry) {
        const display = this.querySelector('.js-shipping-rate');
        display.querySelector('.js-shipping-rate-heading').hidden = true;
        display.querySelector('.js-shipping-rate-text').textContent = this.dataset.countryHelp;
        display.hidden = false;
        return;
      }
      this.controller = new AbortController();
      const signal = this.controller.signal;
      const params = new URLSearchParams();
      for (const [key, selector] of Object.entries({
        zip: '.js-zip-input', country: '.js-shipping-country', province: '.js-shipping-province',
        city: '.js-city-input', address1: '.js-address-input'
      })) {
        const field = this.querySelector(selector);
        if (field?.value.trim()) params.set(`shipping_address[${key}]`, field.value.trim());
      }
      this.submitButton.disabled = true;
      this.submitButton.setAttribute('aria-disabled', 'true');
      this.submitButton.classList.add('loading');
      const spinner = this.submitButton.querySelector('.loading__spinner');
      spinner?.classList.remove('hidden', 'spinning-complete');
      try {
        const cartUrl = window.routes.cart_url;
        const [rateResponse, cartResponse] = await Promise.all([
          fetch(`${cartUrl}/shipping_rates.json?${params}`, {signal}),
          fetch(`${cartUrl}.js`, {signal, cache: 'no-store'})
        ]);
        if (!rateResponse.ok || !cartResponse.ok) throw new Error('Shipping estimate unavailable');
        const [data, cart] = await Promise.all([rateResponse.json(), cartResponse.json()]);
        if (!Array.isArray(data.shipping_rates)) throw new Error('Invalid rates response');
        if (!signal.aborted && this.isConnected) this.displayRates(data.shipping_rates, cart);
      } catch (error) {
        if (error.name !== 'AbortError' && this.isConnected) {
          const errorDisplay = this.querySelector('.js-shipping-error');
          errorDisplay.querySelector('.js-shipping-error-content').textContent = this.dataset.error;
          errorDisplay.hidden = false;
        }
      } finally {
        this.submitButton.disabled = false;
        this.submitButton.setAttribute('aria-disabled', 'false');
        this.submitButton.classList.remove('loading');
        spinner?.classList.add('hidden', 'spinning-complete');
      }
    }

    displayRates(rates, cart) {
      const display = this.querySelector('.js-shipping-rate');
      const heading = display.querySelector('.js-shipping-rate-heading');
      const text = display.querySelector('.js-shipping-rate-text');
      text.replaceChildren();
      heading.hidden = !rates.length;
      if (rates.length) {
        heading.textContent = window.strings.cart.shippingCalculator[rates.length === 1 ? 'singleRate' : 'multipleRates'];
        const list = document.createElement('ul');
        list.className = 'mt-1 t5';
        const format = new Intl.NumberFormat(document.documentElement.lang || 'en', {style: 'currency', currency: cart.currency});
        rates.forEach((rate) => {
          const shipping = Number(rate.price);
          if (!Number.isFinite(shipping) || shipping < 0) throw new Error('Invalid shipping price');
          // Rates and cart prices use the current cart currency. Do not sum unlike currencies.
          if (rate.currency && rate.currency !== cart.currency) throw new Error('Shipping currency mismatch');
          const item = document.createElement('li');
          item.textContent = `${rate.presentment_name || rate.name}: ${format.format(shipping)}`;
          const total = document.createElement('strong');
          total.className = 'block';
          total.textContent = `${this.dataset.totalLabel}: ${format.format((cart.total_price + Math.round(shipping * 100)) / 100)}`;
          item.appendChild(total);
          list.appendChild(item);
        });
        text.appendChild(list);
      } else {
        text.textContent = this.dataset.noRates;
      }
      display.hidden = false;
    }
  }
  customElements.define('shipping-calculator', ShippingCalculator);
}
